<template>
  <div style="overflow: hidden">
    <form-header
        :show-save-edit="mode === 'EDIT' && (!formConfig.restrictedWrite || permissions[formConfig.effectivePermissionWrite])"
        :save-enabled="name.isValid && !readOnly"
        :edit-enabled="true"
        :save-edit-mode="readOnly ? 'EDIT' : 'SAVE'"
        :show-right-menu="true"
        @save="saveRole(false)"
        @edit="editRole"
        @show-audit-log="showAuditLog"
    >
    </form-header>
    <b-container>
      <b-row>
        <b-col>
          <reach-input
              :read-only="readOnly || readOnlyExpression"
              data-test-path="roles-name"
              :label="$tr('rolesEditor.roleName','Role Name')"
              :placeholder="$tr('enterRoleName','Enter role name')"
              :formField="name"
              class="pb-3 pt-3"
          >
          </reach-input>
        </b-col>
      </b-row>
    </b-container>
    <v-box>
      <b-container fluid="true" class="full-height">
        <b-row no-gutters class="full-height">
          <b-col class="full-height">
            <PermissionsEditor
                v-model="assignedPermissions"
                :read-only="readOnly"
            ></PermissionsEditor>
          </b-col>
        </b-row>
      </b-container>
    </v-box>
    <FooterNavbar data-test-path="entity-edit-form-footer">
      <CloseButton @close="close" :read-only="readOnly"></CloseButton>
      <FooterButton
          v-if="!readOnly && (!formConfig.restrictedWrite || permissions[formConfig.effectivePermissionWrite])"
          type="Save"
          :disabled="!name.isValid"
          @save="saveRole"
          :long-size="true"
          :label=" mode === 'NEW' ? (partiallySavedNew ? RETRY_ADD_AND_CLOSE_LABEL: ADD_AND_CLOSE_LABEL) : SAVE_AND_CLOSE_LABEL"
      >
        <template v-slot:icon>
          <font-awesome-layers>
            <font-awesome-icon icon="caret-left" transform="shrink-0 left-7"/>
            <font-awesome-icon icon="save" transform="shrink-0 right-5 down-0"/>
          </font-awesome-layers>
        </template>
      </FooterButton>
    </FooterNavbar>
  </div>
</template>

<script>
import VBox from "@/components/VBox";
import PermissionsSet from "@/server/PermissionsSet";
import Activity from "@/activity/Activity";
import FooterNavbar from "@/components/navbar/FooterNavbar";
import Perms from "@/server/Perms";
import PermissionsEditor from "@/components/list/PermissionsEditor";
import Rpc from "@/Rpc";
import PermissionInfo from "@/server/com/silkplm/rpc/dto/PermissionInfo";
import FooterButton from "@/components/navbar/FooterButton";
import CloseButton from "@/components/navbar/CloseButton";
import SaveAndCloseButton from "@/components/navbar/SaveAndCloseButton";
import FormHeader from "@/components/forms/FormHeader";
import ReachInput from "@/components/inputs/ReachInput";
import ValidatedField from "@/validation/ValidatedField";
import ValidatedForm from "@/validation/ValidatedForm";
import Role from "@/server/com/silkplm/model/users/Role";
import FormsLoader from "@/components/forms/FormsLoader";
import FormMixin from "@/components/forms/customizations/mixins/FormMixin";
import FormEditActivity from "@/activity/templates/layouts/forms/FormEditActivity";
import FormsListActivity from "@/activity/templates/layouts/forms/FormsListActivity";
import FormsAI from "@/components/forms/FormsAI";
import ActivityManager from "@/activity/ActivityManager";
import _ from "lodash";


const AuditLog = () => require("@/components/audit-log/AuditLog");

const FORM_TYPE = Role;

export default {
  name: "RolesEditor",
  components: {
    ReachInput, FormMixin, FormEditActivity, FormsListActivity, VBox,
    PermissionsEditor, FormHeader, FooterNavbar, CloseButton, SaveAndCloseButton, FooterButton
  },
  permission: () => FormsLoader.formPermission(FORM_TYPE),

  async mounted() {
    this.name = new ValidatedField(new ValidatedForm(), "", () =>
        Rpc.Roles.validateRoleName(this.name.value, this.entityId));
    this.name.label = $tr('roleName','Role name');
    if (this.mode === "EDIT") {
      this.role = await Rpc.Roles.listRole(this.entityId).lock();

      this.readOnly = true;
      if (this.role) {
        this.name.value = this.role.name;
        await this.loadAssignedPermissions();
      }
    } else if (this.mode === "NEW") {
      this.readOnly = false;
      await this.name.validate();
    }
    if (this.role?.id) {
      if (this.role.name === "Owner" || this.role.name === "Integration Hub User" || this.role.name === "Integration Hub Trigger" ||
          this.role.name === "WMS Estimation Controller") {
        this.readOnlyExpression = true;
      }
    }
  },

  methods: {

    ...Activity.methods,

    async canClose() {
      return await DialogManager.canClose(!this.readOnly);
    },

    async showAuditLog() {
      let entityLabel = await FormsAI.getEntityLabel(this.formConfig, this.role);
      await ActivityManager.showActivity(AuditLog, {
        entityInstance: this.role,
        formClass: this.formConfig.formClassLong,
        entityLabel: entityLabel
      });
    },

    getTitle() {
      if (this.mode === "NEW") {
        return $tr('rolesEditor.new','New ') + this.formConfig.entityName;
      } else {
        return $tr('rolesEditor.edit','Edit ') + (this.role?.name ?? "");
      }
    },


    beforeClose() {
      if (this.role) {
        this.setResult(this.role);
      }
    },

    async loadAssignedPermissions() {
      for (const k in Perms) {
        this.allPermissions.push(Perms[k]);
      }
      let permission = null;
      for (let i = 0; i < this.role.permissions?.length; i++) {
        permission = _.find(this.allPermissions, {'name': this.role.permissions[i]});
        if (!permission) {
          let message = $tr('roleEditor.permissions.disappeared','Permissions the permission has disappeared from the system. Update the role - ') +
              this.role.name + " - " + this.role.permissions[i].label + ".";
          await Rpc.plm.services.RoleService.sendSlackNotificationAndLogErrorRemote(message);
          continue;
        }
        this.assignedPermissions.push(permission);
      }
      for (let i = 0; i < this.role.dynamicPermissions?.length; i++) {
        permission = _.find(this.allPermissions, {'name': this.role.dynamicPermissions[i].name});
        if (!permission) {
          let message = $tr('roleEditor.permissions.disappeared-','Permissions the permission has disappeared from the system. Update the role ') +
              this.role.name + " - " + this.role.dynamicPermissions[i].label + ".";
          await Rpc.plm.services.RoleService.sendSlackNotificationAndLogErrorRemote(message);
          continue;
        }
        this.assignedPermissions.push(permission);
      }
      this.assignedPermissions.sort((a, b) => (a.label > b.label) ? 1 : -1);
    },

    editRole() {
      this.readOnly = false;
    },

    async saveRole(autoClose = true) {
      let permissionsInfo = [];
      let permInfo = null;
      for (let i = 0; i < this.assignedPermissions?.length; i++) {
        permInfo = new PermissionInfo();
        permInfo.code = this.assignedPermissions[i].code;
        permInfo.name = this.assignedPermissions[i].name;
        permInfo.label = this.assignedPermissions[i].label;
        permInfo.type = this.assignedPermissions[i].type;
        permInfo.subType = this.assignedPermissions[i].subtype;
        permInfo.subTypeCode = this.assignedPermissions[i].subTypeCode;
        permissionsInfo.push(permInfo);
      }
      if (this.mode === "EDIT") {
        this.role.name = this.name.value;
        this.role = await Rpc.Roles.updateRole(this.role, permissionsInfo);
      } else if (this.mode === "NEW") {
        this.role = await Rpc.Roles.addRole(this.name.value, permissionsInfo);

      }
      this.readOnly = true;
      if (autoClose) {
        this.close();
      }
    },

  },

  props: {
    mode: {type: String, default: ""},
    entityId: {default: null},
  },

  data: function () {
    return {
      FORM_TYPE,
      formConfig: FormsLoader.detectFormConfig(FORM_TYPE),
      partiallySavedNew: false,
      SAVE_AND_CLOSE_LABEL:$tr("Save","Save"),
      RETRY_ADD_AND_CLOSE_LABEL: $tr("RetryAdd","Retry add"),
      ADD_AND_CLOSE_LABEL: $tr("add","Add"),

      permissions: new PermissionsSet(),
      assignedPermissions: [],
      allPermissions: [],
      role: null,
      readOnly: true,
      name: new ValidatedField(),
      readOnlyExpression: false,
    }
  }

}
</script>

<style scoped>

</style>